import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { PageHeader, Section, EmptyState } from "../../components/Common";
import { toast } from "sonner";
import { Plus, Trash2, BookOpen, Pencil, X } from "lucide-react";
import { useDataSync } from "../../lib/realtime";
import { formatCurrency } from "../../lib/currency";

const emptyForm = {
  name: "", subject: "", teacher_id: "", description: "",
  schedule_days: "", schedule_time: "", start_date: "", end_date: "",
  default_fee: "",
};

export default function AdminBatches() {
  const [batches, setBatches] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [studentsByBatch, setStudentsByBatch] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const [b, t, s] = await Promise.all([
      api.get("/admin/batches"),
      api.get("/admin/users", { params: { role: "teacher" } }),
      api.get("/admin/users", { params: { role: "student" } }),
    ]);
    setBatches(b.data);
    setTeachers(t.data);
    const map = {};
    (s.data || []).forEach((st) => {
      (st.batch_ids || []).forEach((bid) => {
        map[bid] = (map[bid] || 0) + 1;
      });
    });
    setStudentsByBatch(map);
  };
  useEffect(() => { load(); }, []);
  useDataSync(["batches", "users"], () => load());

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      // Convert numeric field
      if (payload.default_fee === "" || payload.default_fee === null) delete payload.default_fee;
      else payload.default_fee = parseFloat(payload.default_fee);
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      if (editing) {
        await api.patch(`/admin/batches/${editing.id}`, payload);
        toast.success("Batch updated");
      } else {
        // create requires name + subject
        if (!payload.name || !payload.subject) throw new Error("name / subject required");
        await api.post("/admin/batches", payload);
        toast.success("Batch created");
      }
      setForm(emptyForm);
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const startEdit = (b) => {
    setEditing(b);
    setForm({
      name: b.name || "", subject: b.subject || "", teacher_id: b.teacher_id || "",
      description: b.description || "", schedule_days: b.schedule_days || "",
      schedule_time: b.schedule_time || "", start_date: b.start_date || "",
      end_date: b.end_date || "", default_fee: b.default_fee ?? "",
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!confirm("Delete this batch? Students remain but lose the assignment.")) return;
    await api.delete(`/admin/batches/${id}`);
    toast.success("Batch deleted");
    load();
  };

  return (
    <div>
      <PageHeader
        title="Batches"
        subtitle="Groups by class, subject, schedule and teacher."
        actions={
          <button onClick={() => { setShowForm((v) => !v); setEditing(null); setForm(emptyForm); }} data-testid="btn-add-batch"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700">
            <Plus className="h-4 w-4" /> Add batch
          </button>
        }
      />

      {showForm && (
        <Section title={editing ? `Edit: ${editing.name}` : "Create batch"} actions={
          <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="p-1.5 rounded-lg hover:bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        }>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="batch-form">
            <F label="Name" v={form.name} on={(v) => setForm({ ...form, name: v })} tid="batch-name" required />
            <F label="Subject" v={form.subject} on={(v) => setForm({ ...form, subject: v })} tid="batch-subject" required />
            <div className="sm:col-span-2">
              <label className="text-sm font-semibold text-slate-700">Teacher</label>
              <select value={form.teacher_id} onChange={(e) => setForm({ ...form, teacher_id: e.target.value })}
                data-testid="batch-teacher" className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none">
                <option value="">— none —</option>
                {teachers.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.email})</option>)}
              </select>
              {editing && editing.teacher_id && form.teacher_id !== editing.teacher_id && (
                <p className="mt-1 text-xs text-amber-600">Changing teacher for this batch</p>
              )}
            </div>
            <F label="Schedule days (e.g. Mon,Wed,Fri)" v={form.schedule_days} on={(v) => setForm({ ...form, schedule_days: v })} tid="batch-days" />
            <F label="Schedule time (e.g. 16:00-17:30)" v={form.schedule_time} on={(v) => setForm({ ...form, schedule_time: v })} tid="batch-time" />
            <F label="Start date" v={form.start_date} type="date" on={(v) => setForm({ ...form, start_date: v })} tid="batch-start" />
            <F label="End date" v={form.end_date} type="date" on={(v) => setForm({ ...form, end_date: v })} tid="batch-end" />
            <F label="Default fee (₹)" v={form.default_fee} type="number" on={(v) => setForm({ ...form, default_fee: v })} tid="batch-fee" />
            <div className="sm:col-span-2">
              <label className="text-sm font-semibold text-slate-700">Description</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2} data-testid="batch-desc"
                className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
            </div>
            <div className="sm:col-span-2 flex gap-2">
              <button type="submit" data-testid="batch-submit" className="px-5 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700">
                {editing ? "Save changes" : "Create"}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="px-5 py-2.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50">
                Cancel
              </button>
            </div>
          </form>
        </Section>
      )}

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {batches.map((b) => {
          const teacher = teachers.find((t) => t.id === b.teacher_id);
          const studentCount = studentsByBatch[b.id] || 0;
          return (
            <div key={b.id} data-testid={`batch-${b.id}`} className="bg-white border border-slate-200 rounded-xl p-5 ym-card-hover">
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 grid place-items-center">
                  <BookOpen className="h-5 w-5" />
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(b)} className="text-blue-600 hover:bg-blue-50 p-1.5 rounded-lg" data-testid={`edit-batch-${b.id}`} title="Edit batch">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => remove(b.id)} className="text-rose-500 hover:bg-rose-50 p-1.5 rounded-lg" data-testid={`delete-batch-${b.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <h3 className="mt-4 font-heading font-bold text-lg text-slate-900">{b.name}</h3>
              <p className="text-sm text-slate-500">{b.subject}</p>
              {b.description && <p className="mt-2 text-xs text-slate-500 line-clamp-2">{b.description}</p>}
              <div className="mt-3 space-y-1 text-xs">
                {teacher && <div><span className="text-slate-500">Teacher:</span> <span className="font-semibold text-slate-700">{teacher.name}</span></div>}
                {(b.schedule_days || b.schedule_time) && (
                  <div><span className="text-slate-500">Schedule:</span> <span className="font-semibold text-slate-700">{[b.schedule_days, b.schedule_time].filter(Boolean).join(" · ")}</span></div>
                )}
                {(b.start_date || b.end_date) && (
                  <div><span className="text-slate-500">Duration:</span> <span className="font-semibold text-slate-700">{b.start_date || "—"} → {b.end_date || "—"}</span></div>
                )}
                {b.default_fee != null && b.default_fee !== "" && (
                  <div><span className="text-slate-500">Default fee:</span> <span className="font-semibold text-slate-700">{formatCurrency(b.default_fee)}</span></div>
                )}
                <div><span className="text-slate-500">Students:</span> <span className="font-semibold text-slate-700">{studentCount}</span></div>
              </div>
            </div>
          );
        })}
        {batches.length === 0 && (
          <div className="sm:col-span-2 lg:col-span-3">
            <EmptyState title="No batches yet" desc="Create a batch to organize students and teachers." icon={BookOpen} />
          </div>
        )}
      </div>
    </div>
  );
}

function F({ label, v, on, type = "text", tid, required }) {
  return (
    <div>
      <label className="text-sm font-semibold text-slate-700">{label}</label>
      <input type={type} required={required} value={v ?? ""} onChange={(e) => on(e.target.value)}
        data-testid={tid}
        className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
    </div>
  );
}
